import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "./components/auth/AuthProvider";
import { ProtectedRoute } from "./components/protected-route";
import { AuthStateMonitor } from "./components/auth-state-monitor";
import { AuthNavigationHandler } from "./components/auth-navigation-handler";
import { ScrollToTop } from "./components/scroll-to-top";
import { DashboardProvider } from "./contexts/dashboard-context";
import { ProfileProvider } from "./contexts/profile-context";
import { GlobalProfileCompletionDialog } from "./components/profile/global-profile-completion-dialog";
import { GlobalUserTypeSelectionDialog } from "./components/profile/global-user-type-selection-dialog";
import { ComprehensionAssessmentProvider } from "./components/assessment/comprehension-assessment-provider";
import { AssessmentNotificationSystem } from "./components/assessment/assessment-notification-system";
import { AppErrorBoundary } from "./components/app-error-boundary";
import { PageErrorBoundary } from "./components/page-error-boundary";

// Layouts
import { AppLayout } from "./layouts/app-layout";
import { AuthLayout } from "./layouts/auth-layout";

// Pages
import { HomePage } from "./pages/home-page";
import { LoginPage } from "./pages/login-page";
import { SignupPage } from "./pages/signup-page";
import { ForgotPasswordPage } from "./pages/forgot-password-page";
import { ResetPasswordPage } from "./pages/reset-password-page";
import { BeaconsBrowserPage } from "./pages/beacons-browser";
import { BeaconChatPage } from "./pages/beacon-chat";
import { DashboardPage } from "./pages/dashboard";
import { ProfilePage } from "./pages/profile";
import { ComprehensionAssessmentPage } from "./pages/comprehension-assessment";
import { ComprehensionAssessmentFeaturePage } from "./pages/comprehension-assessment-feature-page";
import { AuthCallbackPage } from "./pages/auth-callback-page";
import { AboutPage } from "./pages/about-page";
import { ContactPage } from "./pages/contact-page";
import { PrivacyPolicyPage } from "./pages/privacy-policy-page";
import { TermsOfServicePage } from "./pages/terms-of-service-page";
import { HelpPage } from "./pages/help-page";
import { KnowledgeBeaconsPage } from "./pages/knowledge-beacons-page";
import { SparksFeaturePage } from "./pages/sparks-feature-page";
import SparksPage from "./pages/sparks-page";
import LatexPage from "./pages/latex-demo";

function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <ProfileProvider>
            <DashboardProvider>
              <ComprehensionAssessmentProvider>
                <ScrollToTop />
                <AuthNavigationHandler />
          <Routes>
            {/* Home page */}
            <Route path="/" element={
              <PageErrorBoundary pageName="Home">
                <HomePage />
              </PageErrorBoundary>
            } />
            
            {/* Auth pages */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={
                <PageErrorBoundary pageName="Login">
                  <LoginPage />
                </PageErrorBoundary>
              } />
              <Route path="/signup" element={
                <PageErrorBoundary pageName="Signup">
                  <SignupPage />
                </PageErrorBoundary>
              } />
              <Route path="/forgot-password" element={
                <PageErrorBoundary pageName="Forgot Password">
                  <ForgotPasswordPage />
                </PageErrorBoundary>
              } />
              <Route path="/reset-password" element={
                <PageErrorBoundary pageName="Reset Password">
                  <ResetPasswordPage />
                </PageErrorBoundary>
              } />
              <Route path="/auth/callback" element={
                <PageErrorBoundary pageName="Auth Callback">
                  <AuthCallbackPage />
                </PageErrorBoundary>
              } />
            </Route>
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={
                  <PageErrorBoundary pageName="Dashboard">
                    <DashboardPage />
                  </PageErrorBoundary>
                } />
                <Route path="/dashboard/chat/:chatId" element={
                  <PageErrorBoundary pageName="Chat">
                    <DashboardPage />
                  </PageErrorBoundary>
                } />
                <Route path="/profile" element={
                  <PageErrorBoundary pageName="Profile">
                    <ProfilePage />
                  </PageErrorBoundary>
                } />
                <Route path="/assessment" element={
                  <PageErrorBoundary pageName="Comprehension Assessment">
                    <ComprehensionAssessmentPage />
                  </PageErrorBoundary>
                } />
                <Route path="/beacons" element={
                  <PageErrorBoundary pageName="Knowledge Beacons Browser">
                    <BeaconsBrowserPage />
                  </PageErrorBoundary>
                } />
                <Route path="/beacons/learn/:beaconId" element={
                  <PageErrorBoundary pageName="Beacon Learning Session">
                    <BeaconChatPage />
                  </PageErrorBoundary>
                } />
                <Route path="/beacons/learn/:beaconId/chat/:chatId" element={
                  <PageErrorBoundary pageName="Beacon Chat Session">
                    <BeaconChatPage />
                  </PageErrorBoundary>
                } />
                <Route path="/sparks" element={
                  <PageErrorBoundary pageName="Subject Sparks">
                    <SparksPage />
                  </PageErrorBoundary>
                } />
              </Route>
            </Route>
            
            {/* Static/Public pages */}
            <Route path="/knowledge-beacons" element={
              <PageErrorBoundary pageName="Knowledge Beacons">
                <KnowledgeBeaconsPage />
              </PageErrorBoundary>
            } />
            <Route path="/comprehension-assessment" element={
              <PageErrorBoundary pageName="Comprehension Assessment">
                <ComprehensionAssessmentFeaturePage />
              </PageErrorBoundary>
            } />
            <Route path="/discover-sparks" element={
              <PageErrorBoundary pageName="Discover Sparks">
                <SparksFeaturePage />
              </PageErrorBoundary>
            } />
            <Route path="/about" element={
              <PageErrorBoundary pageName="About">
                <AboutPage />
              </PageErrorBoundary>
            } />
            <Route path="/contact" element={
              <PageErrorBoundary pageName="Contact">
                <ContactPage />
              </PageErrorBoundary>
            } />
            <Route path="/privacy-policy" element={
              <PageErrorBoundary pageName="Privacy Policy">
                <PrivacyPolicyPage />
              </PageErrorBoundary>
            } />
            <Route path="/terms-of-service" element={
              <PageErrorBoundary pageName="Terms of Service">
                <TermsOfServicePage />
              </PageErrorBoundary>
            } />
            <Route path="/help" element={
              <PageErrorBoundary pageName="Help">
                <HelpPage />
              </PageErrorBoundary>
            } />
            <Route path="/latex" element={
              <PageErrorBoundary pageName="LaTeX">
                <LatexPage />
              </PageErrorBoundary>
            } />
            
            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Assessment Notification System */}
          <AssessmentNotificationSystem />
          
          {/* Auth State Monitor - only visible in development with issues */}
          {import.meta.env.DEV && <AuthStateMonitor />}
              </ComprehensionAssessmentProvider>
            </DashboardProvider>
          
          {/* Global Profile Completion Dialog */}
          <GlobalProfileCompletionDialog />
          
          {/* Global User Type Selection Dialog for Social Login */}
          <GlobalUserTypeSelectionDialog />
          </ProfileProvider>
        </AuthProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App